(() => {
  if (window.fleximaaCollectionNavigation) return;
  const allowedHandles = new Set([
    'womens', 'womens-plain-t-shirt', 'womens-printed-t-shirt', 'womens-crop-top', 'womens-long-top',
    'mens', 'mens-plain-t-shirt', 'mens-printed-t-shirt', 'mens-polo-neck-t-shirt', 'mens-hoodies',
  ]);
  const cache = new Map();
  let sequence = 0;
  let displayedUrl = window.location.href;
  let pendingUrl = null;
  let controller;
  const main = () => document.getElementById('MainContent');
  const eligible = (url) => url.origin === window.location.origin &&
    /^\/([^/]+\/)?collections\/[^/]+$/.test(url.pathname) &&
    allowedHandles.has(url.pathname.split('/').at(-1)) && !url.search && !url.hash;
  const ready = () => !!main()?.querySelector('.fleximaa-category-navigation');
  const connection = navigator.connection;
  const allowPrefetch = () => !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');
  const scriptsCompatible = (parsed) => {
    const loaded = new Set(Array.from(document.querySelectorAll('script[src]'), (node) => new URL(node.src).pathname));
    return Array.from(parsed.querySelectorAll('#MainContent script[src]')).every((node) =>
      loaded.has(new URL(node.getAttribute('src'), window.location.href).pathname));
  };
  const load = (url, signal) => fetch(url.href, { signal, credentials: 'same-origin' }).then(async (response) => {
    if (!response.ok || new URL(response.url).origin !== url.origin || new URL(response.url).pathname !== url.pathname) throw new Error('Collection unavailable');
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (!parsed.getElementById('MainContent')?.querySelector('.fleximaa-category-navigation') ||
        !parsed.querySelector('#product-grid') || !scriptsCompatible(parsed)) throw new Error('Use normal navigation');
    return parsed;
  });
  const warm = (url) => {
    if (!eligible(url) || !ready() || !allowPrefetch() || url.href === displayedUrl || cache.has(url.href)) return;
    if (cache.size >= 3) cache.delete(cache.keys().next().value);
    const entry = { time: Date.now() };
    entry.promise = load(url).catch(() => { if (cache.get(url.href) === entry) cache.delete(url.href); return null; });
    cache.set(url.href, entry);
  };
  const applyHead = (parsed) => {
    document.title = parsed.title;
    const selectors = ['link[rel="canonical"]', 'meta[name="description"]', 'meta[name="robots"]',
      'meta[property^="og:"]', 'meta[name^="twitter:"]'];
    selectors.forEach((selector) => {
      document.head.querySelectorAll(selector).forEach((node) => node.remove());
      parsed.head.querySelectorAll(selector).forEach((node) => document.head.appendChild(document.importNode(node, true)));
    });
  };
  const markPending = (url) => {
    main()?.setAttribute('aria-busy', 'true');
    document.querySelectorAll('.fleximaa-category-navigation a').forEach((link) =>
      link.classList.toggle('is-pending', link.href === url?.href));
  };
  const clearPending = () => {
    main()?.removeAttribute('aria-busy');
    document.querySelectorAll('.fleximaa-category-navigation .is-pending').forEach((link) => link.classList.remove('is-pending'));
  };
  const navigate = async (url, options = {}) => {
    if (!eligible(url) || !ready()) return false;
    if (pendingUrl === url.href) return true;
    const thisSequence = ++sequence;
    controller?.abort();
    controller = new AbortController();
    const activeController = controller;
    pendingUrl = url.href;
    if (url.href === displayedUrl) { pendingUrl = null; clearPending(); return true; }
    markPending(url);
    let timeout;
    try {
      const entry = cache.get(url.href);
      const request = entry && Date.now() - entry.time < 30000
        ? entry.promise : load(url, activeController.signal);
      const parsed = await Promise.race([request, new Promise((resolve, reject) => {
        timeout = setTimeout(() => { activeController.abort(); reject(new Error('Collection timeout')); }, 8000);
      })]);
      if (thisSequence !== sequence) return true;
      if (!parsed) throw new Error('Collection unavailable');
      cache.delete(url.href);
      const currentMain = main();
      const nextMain = parsed.getElementById('MainContent');
      const active = document.activeElement;
      const restoreKeyboardFocus = options.keyboard && currentMain.contains(active);
      const categoryScroll = currentMain.querySelector('.fleximaa-category-navigation .fleximaa-plain-style-tabs')?.scrollLeft || 0;
      const oldTop = currentMain.getBoundingClientRect().top;
      const oldHeight = currentMain.getBoundingClientRect().height;
      const atTop = window.scrollY < Math.max(200, currentMain.offsetTop);
      currentMain.style.minHeight = `${oldHeight}px`;
      document.querySelectorAll('quick-add-modal[data-section]').forEach((modal) => {
        const section = document.getElementById(`shopify-section-${modal.dataset.section}`);
        if (section && currentMain.contains(section)) modal.remove();
      });
      // Adopt complete inert subtrees so custom-element constructors see their children.
      currentMain.replaceChildren(...Array.from(nextMain.childNodes).map((node) => document.adoptNode(node)));
      applyHead(parsed);
      if (!options.history) history.pushState({ ...history.state, fleximaaCollection: true }, '', url.href);
      displayedUrl = url.href;
      pendingUrl = null;
      const tabs = currentMain.querySelector('.fleximaa-category-navigation .fleximaa-plain-style-tabs');
      if (tabs) tabs.scrollLeft = categoryScroll;
      clearPending();
      document.dispatchEvent(new CustomEvent('fleximaa:collection-grid-rendered'));
      currentMain.style.minHeight = '';
      if (!options.history && !atTop) window.scrollTo({ top: window.scrollY + currentMain.getBoundingClientRect().top - oldTop, behavior: 'instant' });
      if (restoreKeyboardFocus) currentMain.querySelector('.fleximaa-category-navigation a[aria-current="page"]')?.focus({ preventScroll: true });
      clearPending();
    } catch (error) {
      if (thisSequence !== sequence) return true;
      window.location.assign(url.href);
    } finally {
      clearTimeout(timeout);
      if (thisSequence === sequence) { pendingUrl = null; clearPending(); }
    }
    return true;
  };
  document.addEventListener('pointerover', (event) => {
    const link = event.target.closest('.fleximaa-category-navigation a');
    if (link) warm(new URL(link.href));
  }, { passive: true });
  document.addEventListener('focusin', (event) => {
    const link = event.target.closest('.fleximaa-category-navigation a');
    if (link) warm(new URL(link.href));
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey ||
        event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href);
    if (!eligible(url) || !ready()) return;
    event.preventDefault();
    const menu = link.closest('header-drawer');
    if (menu && typeof menu.closeMenuDrawer === 'function') menu.closeMenuDrawer(event, menu.querySelector('summary'));
    else link.closest('details[open]')?.removeAttribute('open');
    navigate(url, { keyboard: event.detail === 0 });
  });
  window.addEventListener('popstate', () => {
    const url = new URL(window.location.href);
    if (url.href !== displayedUrl || pendingUrl) {
      if (!eligible(url) || !ready()) window.location.reload();
      else navigate(url, { history: true });
    }
  });
  window.fleximaaCollectionNavigation = { navigate };
})();
